import React, { useMemo, useCallback, useState } from 'react';
import { Database, Github } from 'lucide-react';
import LoadingRow from './subComponents/results/states/LoadingRow';
import ErrorCard from './subComponents/results/states/ErrorCard';
import { useCompactViewport } from '../../../hooks/useCompactViewport';
import MessageDialogModal from '../../modals/MessageDialogModal';

const buildRepoLink = (repository_url, branch) => {
  if (!repository_url) return null;
  const baseUrl = repository_url
    .replace(/\.git\/?$/, '')
    .replace(/\/+$/, '');

  return branch
    ? `${baseUrl}/tree/${encodeURIComponent(branch)}`
    : baseUrl;
};

const modelTabKey = (m) =>
  `${m?.model_id ?? ''}:${m?.repository_branch_name ?? ''}`;

const ResultsTab = ({
  vedaModels = [],
  vedaModelsLoading,
  vedaModelsError,
  loadModelViews,
  selectedModel,
  modelViews = [],
  modelViewsLoading,
  modelViewsError,
  setActivePresenterView
}) => {
  const isCompactViewport = useCompactViewport();
  const [presenterDesktopOnlyOpen, setPresenterDesktopOnlyOpen] = useState(false);

  /* MODELS ERROR */
  const modelsErrorComponent = useMemo(() => {
    if (!vedaModelsError) return null;
    return (
      <ErrorCard
        title="Failed to load models"
        message={vedaModelsError}
      />
    );
  }, [vedaModelsError]);

  /* MODELS LOADING */
  const modelsLoadingComponent = useMemo(() => {
    if (!vedaModelsLoading || vedaModelsError) return null;
    return <LoadingRow message="Fetching model names…" />;
  }, [vedaModelsLoading, vedaModelsError]);

  /* MODEL VIEWS LOADING */
  const modelViewsLoadingComponent = useMemo(() => {
    if (!modelViewsLoading || modelViewsError) return null;

    return (
      <div className="flex items-center gap-3 text-gray-700">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500 shrink-0" />
        <div className="text-xs sm:text-sm min-w-0">
          Loading saved views…
        </div>
      </div>
    );
  }, [modelViewsLoading, modelViewsError]);

  /* HANDLE OPEN PRESENTER VIEW (desktop only; mobile/tablet show notice) */
  const handleOpenPresenterView = useCallback(
    (view) => {
      if (isCompactViewport) {
        setPresenterDesktopOnlyOpen(true);
        return;
      }
      setActivePresenterView({
        id: view.presenter_link_id,
        viewId: view.view_id,
        name:
          view.name ||
          selectedModel?.repository_branch_name ||
          ''
      });
    },
    [isCompactViewport, setActivePresenterView, selectedModel]
  );

  /* MODEL VIEW ROWS */
  const modelViewRows = useMemo(() => {
    return modelViews.map((v) => (
      <tr key={v.view_id}>
        <td className="px-[6px] py-[6px] sm:px-[15px] sm:py-[11px] text-left align-middle text-[10px] sm:text-xs md:text-sm text-gray-900 min-w-0 whitespace-normal break-words [text-wrap:auto]">
          {v.presenter_link_id ? (
            <button
              type="button"
              className="block w-full min-w-0 text-left whitespace-normal break-words [text-wrap:auto] text-[10px] sm:text-xs md:text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
              title="Open presenter view"
              onClick={() => handleOpenPresenterView(v)}
            >
              {v.name || '—'}
            </button>
          ) : (
            <span className="block w-full text-left whitespace-normal break-words [text-wrap:auto]">{v.name || '—'}</span>
          )}
        </td>
        <td className="px-[6px] py-[6px] sm:px-[15px] sm:py-[11px] text-left align-middle text-[10px] sm:text-xs md:text-sm text-gray-700 min-w-0 whitespace-normal break-words [text-wrap:auto]">
          <span className="block w-full text-left whitespace-normal break-words [text-wrap:auto]" title={v.description || ''}>
            {v.description || '—'}
          </span>
        </td>
        <td className="px-[6px] py-[6px] sm:px-[15px] sm:py-[11px] text-left align-middle text-[10px] sm:text-xs md:text-sm text-gray-700 min-w-0 whitespace-normal break-words [text-wrap:auto]">
          <span className="block w-full text-left whitespace-normal break-words [text-wrap:auto]">
            {v.edited_on
              ? new Date(v.edited_on).toLocaleString()
              : '—'}
          </span>
        </td>
      </tr>
    ));
  }, [modelViews, handleOpenPresenterView]);

  const isModelActive = useCallback(
    (m) =>
      !!selectedModel && modelTabKey(selectedModel) === modelTabKey(m),
    [selectedModel]
  );

  const selectedRepoHref = useMemo(() => {
    if (!selectedModel) return null;
    return buildRepoLink(
      selectedModel.repository_url,
      selectedModel.repository_branch_name
    );
  }, [selectedModel]);

  return (
    <div className="w-full min-w-0 overflow-hidden px-[7px] md:px-4 lg:px-6 py-[7px] md:py-4 lg:py-6">
      <div className="tab-content w-full min-w-0">
        <div className="tab-visualization w-full min-w-0">
          {/* MODELS ERROR */}
          {modelsErrorComponent}

          {/* MODELS LOADING */}
          {modelsLoadingComponent}

          {/* MODEL TABS */}
          {!vedaModelsError && !vedaModelsLoading && (
            <div className="card overflow-hidden w-full min-w-0">
              <div className="p-[10px] sm:p-[15px] border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-xs text-gray-500">
                  {vedaModels.length} model(s)
                </div>
              </div>

              {vedaModels.length === 0 ? (
                <div className="p-6 md:p-8 text-center text-gray-600">
                  <Database className="h-9 w-9 sm:h-10 sm:w-10 mx-auto mb-3 text-gray-300" />
                  <div className="text-sm md:text-base font-semibold">
                    No models found
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1 px-[7px]">
                    VedaOnline returned 0 model branches for this ISO.
                  </div>
                </div>
              ) : (
                <div className="border-b border-gray-100 bg-gray-50/80 min-w-0">
                  <div
                    className="flex overflow-x-auto overflow-y-hidden gap-0 px-[7px] pt-[7px] pb-0 min-w-0"
                    role="tablist"
                    aria-label="VedaOnline models"
                  >
                    {vedaModels.map((m) => {
                      const active = isModelActive(m);
                      return (
                        <button
                          key={modelTabKey(m)}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          className={[
                            'shrink-0 px-[11px] py-[7px] sm:px-4 sm:py-2.5 text-xs sm:text-sm font-mono border-b-2 -mb-px transition-colors',
                            active
                              ? 'border-indigo-600 text-indigo-700 bg-white'
                              : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                          ].join(' ')}
                          onClick={() => loadModelViews(m)}
                        >
                          {m.repository_branch_name}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab panel: views + model source for selected model */}
                  {selectedModel && (
                    <div className="bg-white border-t border-gray-100 min-w-0">
                      <div className="p-[10px] sm:p-[15px] border-b border-gray-200 flex flex-row flex-nowrap items-center justify-between gap-2 sm:gap-4 min-w-0">
                        <div className="text-sm md:text-base font-semibold text-gray-900 truncate min-w-0 flex-1">
                          Views
                        </div>
                        <div className="flex shrink-0 items-center">
                          {selectedRepoHref ? (
                            <a
                              href={selectedRepoHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-[9px] py-[5px] sm:px-2.5 sm:py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900"
                              aria-label="Open model source repository"
                            >
                              <Github className="h-3.5 w-3.5 shrink-0 text-gray-700" aria-hidden />
                              Model Source
                            </a>
                          ) : (
                            <button
                              type="button"
                              disabled
                              title="No repository URL available for this model"
                              className="inline-flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50 px-[9px] py-[5px] sm:px-2.5 sm:py-1.5 text-xs font-medium text-gray-400 cursor-not-allowed"
                            >
                              <Github className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
                              Model Source
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="p-[11px] sm:p-4 w-full min-w-0">
                        {modelViewsError && (
                          <div className="mb-3 text-xs sm:text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-[11px] py-[7px] sm:px-3 sm:py-2 break-words">
                            {modelViewsError}
                          </div>
                        )}

                        {modelViewsLoadingComponent}

                        {!modelViewsLoading && !modelViewsError && modelViews.length === 0 && (
                          <div className="text-xs sm:text-sm text-gray-600">
                            No saved views found for this model.
                          </div>
                        )}

                        {!modelViewsLoading && !modelViewsError && modelViews.length > 0 && (
                          <div className="overflow-x-auto overflow-y-hidden min-w-0 w-full -mx-[3px] px-[3px] sm:mx-0 sm:px-0">
                            <table className="min-w-full text-[10px] sm:text-xs md:text-sm table-fixed w-full">
                              <thead className="bg-gray-50">
                                <tr className="text-left text-[10px] sm:text-xs md:text-sm text-gray-500 uppercase tracking-wide">
                                  <th scope="col" className="w-[30%] px-[6px] py-[6px] sm:px-[15px] sm:py-[11px] font-medium text-left align-middle whitespace-normal break-words [text-wrap:auto]">
                                    View name
                                  </th>
                                  <th scope="col" className="w-[45%] px-[6px] py-[6px] sm:px-[15px] sm:py-[11px] font-medium text-left align-middle whitespace-normal break-words [text-wrap:auto]">
                                    Description
                                  </th>
                                  <th scope="col" className="w-[25%] px-[6px] py-[6px] sm:px-[15px] sm:py-[11px] font-medium text-left align-middle whitespace-normal break-words [text-wrap:auto]">
                                    Edited on
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {modelViewRows}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <MessageDialogModal
        open={presenterDesktopOnlyOpen}
        onClose={() => setPresenterDesktopOnlyOpen(false)}
        title="Open on Desktop"
        description={
          <>
            Please switch to a desktop or laptop to see the results.
          </>
        }
        confirmButtonText="OK"
      />
    </div>
  );
};

export default ResultsTab;